import {
  faCaretLargeLeft,
  faCaretLargeRight
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  alpha,
  Box,
  Card,
  CardContent,
  CardHeader,
  Stack,
  Typography,
  useTheme
} from '@mui/material';
import type { RowClassParams } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { useCallback } from 'react';

import REPORT_PARAGRAPHS from '@/constants/reportparagraph.constants';
import ReportParagraph from '@/features/ReportsTemplateParagraph/ReportsParagraph';
import { useGridTheme } from '@/hooks/useGridTheme';
import type { TicketMetricsByPriority } from '@/pages/ReportsTemplates/ReportsTemplate.types';
import {
  REPORTS_METRICS_COLUMNS,
  TICKET_STAGE_COLORS,
  TICKET_STAGES
} from '@/pages/ReportsTemplates/ReportsTemplates.constants';

import useStyles from '../ReportsPages.styles';
import { transformMetricsToRowData } from './TicketsPages.utils';

const TicketsMetrics = ({
  orgAllMetrics
}: {
  orgAllMetrics: TicketMetricsByPriority[];
}) => {
  const gridTheme = useGridTheme();
  const { classes } = useStyles();
  const theme = useTheme();

  const metricsTableTheme = gridTheme.withParams({
    backgroundColor: 'transparent',
    rowBorder: { style: 'none' }
  });

  const rowData = transformMetricsToRowData(orgAllMetrics);

  const getRowStyle = useCallback(
    (params: RowClassParams) => {
      if (params?.data?.group === 'Pondurance') {
        return { background: alpha(theme.palette.primary.light, 0.1) };
      }
      return { background: alpha('#808080', 0.1) };
    },
    [theme]
  );

  return (
    <div className={classes.reportsPageContainer}>
      <Typography className={classes.reportsPreviewSubTitle}>
        MDR Performance and Customer Response Metrics by Ticket Priority
      </Typography>
      <Box className={classes.metricsTableContainer}>
        <AgGridReact
          rowData={rowData}
          columnDefs={REPORTS_METRICS_COLUMNS}
          theme={metricsTableTheme}
          rowHeight={45}
          headerHeight={30}
          enableCellSpan
          getRowStyle={getRowStyle}
        />
      </Box>
      <ReportParagraph>
        {REPORT_PARAGRAPHS.threatTicketMetricsDescription}
      </ReportParagraph>
      <Stack direction="column">
        <Typography className={classes.reportsPreviewSubTitle}>
          Understanding MTTX Metrics
        </Typography>
        <Box>
          <Stack direction="row" sx={{ alignItems: 'center' }}>
            <FontAwesomeIcon
              size="3x"
              icon={faCaretLargeLeft}
              style={{ color: '#000000', marginRight: '-27px' }}
            />
            <Box
              className={classes.metricsArrowBox}
              sx={{ backgroundColor: '#000000', width: '450px' }}
            >
              <Typography className={classes.metricsArrowText}>
                Measures Pondurance
              </Typography>
            </Box>
            <FontAwesomeIcon
              size="3x"
              icon={faCaretLargeRight}
              className={classes.metricsArrow}
              style={{ color: '#000000' }}
            />
          </Stack>
        </Box>
        <Stack direction="row" spacing={0}>
          {TICKET_STAGES.map((stage) => (
            <Card
              className={classes.metricsCard}
              key={stage.label}
              sx={{
                maxWidth: '200px',
                border: `2px solid ${TICKET_STAGE_COLORS[stage.label as keyof typeof TICKET_STAGE_COLORS]}`
              }}
            >
              <CardHeader
                sx={{
                  backgroundColor:
                    TICKET_STAGE_COLORS[
                      stage.label as keyof typeof TICKET_STAGE_COLORS
                    ]
                }}
                className={classes.metricsCardHeader}
                title={stage.label}
              />
              <CardContent className={classes.metricsCardContent}>
                <Typography sx={{ fontSize: '10px' }} variant="body2">
                  {stage.description}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Stack>
        <Stack direction="column" spacing={-1.5}>
          <Stack direction="row" sx={{ alignItems: 'center' }}>
            <Box
              className={classes.metricsArrowBox}
              sx={{
                backgroundColor: TICKET_STAGE_COLORS.Detection,
                width: '140px'
              }}
            >
              <Typography className={classes.metricsArrowText}>MTTD</Typography>
            </Box>
            <FontAwesomeIcon
              size="3x"
              icon={faCaretLargeRight}
              className={classes.metricsArrow}
              style={{
                color: TICKET_STAGE_COLORS.Detection
              }}
            />
          </Stack>
          <Stack direction="row" sx={{ alignItems: 'center' }}>
            <Box
              className={classes.metricsArrowBox}
              sx={{
                backgroundColor: TICKET_STAGE_COLORS.Contained,
                width: '355px'
              }}
            >
              <Typography className={classes.metricsArrowText}>MTTC</Typography>
            </Box>
            <FontAwesomeIcon
              size="3x"
              icon={faCaretLargeRight}
              className={classes.metricsArrow}
              style={{
                color: TICKET_STAGE_COLORS.Contained
              }}
            />
          </Stack>
          <Stack direction="row" sx={{ alignItems: 'center' }}>
            <Box
              className={classes.metricsArrowBox}
              sx={{
                backgroundColor: TICKET_STAGE_COLORS.Escalated,
                width: '465px'
              }}
            >
              <Typography className={classes.metricsArrowText}>MTTE</Typography>
            </Box>
            <FontAwesomeIcon
              size="3x"
              icon={faCaretLargeRight}
              className={classes.metricsArrow}
              style={{
                color: TICKET_STAGE_COLORS.Escalated
              }}
            />
          </Stack>
          <Stack direction="row" sx={{ alignItems: 'center' }}>
            <Box
              className={classes.metricsArrowBox}
              sx={{
                backgroundColor: TICKET_STAGE_COLORS.Acknowledged,
                width: '110px',
                marginLeft: '460px !important'
              }}
            >
              <Typography className={classes.metricsArrowText}>MTTA</Typography>
            </Box>
            <FontAwesomeIcon
              size="3x"
              icon={faCaretLargeRight}
              className={classes.metricsArrow}
              style={{
                color: TICKET_STAGE_COLORS.Acknowledged
              }}
            />
          </Stack>
          <Stack direction="row" sx={{ alignItems: 'center' }}>
            <Box
              className={classes.metricsArrowBox}
              sx={{
                backgroundColor: TICKET_STAGE_COLORS.Remediated,
                width: '220px',
                marginLeft: '460px !important'
              }}
            >
              <Typography className={classes.metricsArrowText}>
                MTTREM
              </Typography>
            </Box>
            <FontAwesomeIcon
              size="3x"
              icon={faCaretLargeRight}
              className={classes.metricsArrow}
              style={{
                color: TICKET_STAGE_COLORS.Remediated
              }}
            />
          </Stack>
        </Stack>
      </Stack>
    </div>
  );
};
export default TicketsMetrics;
